// src/dominio/comprobanteImpreso.js
// Modelo común de lo que se imprime o comparte: ticket de pago, ticket de venta y la
// representación impresa de una boleta o factura electrónica. Un solo modelo para que
// la impresión térmica, el PDF y la vista previa de Ajustes muestren lo mismo.
// Los datos del negocio (razón social, RUC, dirección, contacto, mensaje) los define
// el admin en Ajustes → Ticket y comprobantes (gimnasios/{id}.config.ticket).
// Puro: sin React, Firebase ni DOM.

import { MONEDAS, monedaActual, normalizarMoneda, simboloMoneda } from './moneda';

export const DATOS_TICKET_VACIO = Object.freeze({
  razonSocial: '', ruc: '', direccion: '', telefono: '', correo: '', web: '', mensaje: '', mostrarIgv: true,
});
export const MENSAJE_POR_DEFECTO = '¡Gracias por tu preferencia!';

const LIMITES = { razonSocial: 120, direccion: 160, telefono: 30, correo: 100, web: 100, mensaje: 160 };
const texto = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

/** Valida y limpia los datos del ticket (mismas reglas que firestore.rules). Lanza con un mensaje claro. */
export const normalizarDatosTicket = (datos = {}) => {
  const limpio = { ...DATOS_TICKET_VACIO };
  for (const [campo, max] of Object.entries(LIMITES)) limpio[campo] = texto(datos[campo], max);
  limpio.ruc = String(datos.ruc ?? '').replace(/\D/g, '');
  if (limpio.ruc && limpio.ruc.length !== 11) throw new Error('El RUC tiene 11 dígitos (o déjalo vacío).');
  if (limpio.correo && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(limpio.correo)) throw new Error('Revisa el correo.');
  limpio.web = limpio.web.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  limpio.mostrarIgv = datos.mostrarIgv !== false;
  return limpio;
};

// ── Importe en letras ──────────────────────────────────────────────────────────
const UNIDADES = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE', 'DIEZ', 'ONCE', 'DOCE', 'TRECE',
  'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE', 'VEINTE', 'VEINTIUNO', 'VEINTIDÓS', 'VEINTITRÉS',
  'VEINTICUATRO', 'VEINTICINCO', 'VEINTISÉIS', 'VEINTISIETE', 'VEINTIOCHO', 'VEINTINUEVE'];
const DECENAS = ['', '', '', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
const CENTENAS = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS',
  'OCHOCIENTOS', 'NOVECIENTOS'];

const hasta999 = (n) => {
  if (n === 0) return '';
  if (n === 100) return 'CIEN';
  const c = Math.floor(n / 100), resto = n % 100;
  let decenas = '';
  if (resto < 30) decenas = UNIDADES[resto];
  else decenas = `${DECENAS[Math.floor(resto / 10)]}${resto % 10 ? ` Y ${UNIDADES[resto % 10]}` : ''}`;
  return [CENTENAS[c], decenas].filter(Boolean).join(' ');
};

/** 0 ≤ n < 1 000 000 000 → «MIL DOSCIENTOS TREINTA Y CUATRO». */
export const numeroEnLetras = (n) => {
  const entero = Math.floor(Math.abs(Number(n) || 0));
  if (entero === 0) return 'CERO';
  if (entero >= 1e9) return String(entero);
  const millones = Math.floor(entero / 1e6), miles = Math.floor((entero % 1e6) / 1000), resto = entero % 1000;
  const partes = [];
  if (millones) partes.push(millones === 1 ? 'UN MILLÓN' : `${hasta999(millones).replace(/UNO$/, 'UN')} MILLONES`);
  if (miles) partes.push(miles === 1 ? 'MIL' : `${hasta999(miles).replace(/UNO$/, 'UN')} MIL`);
  if (resto) partes.push(hasta999(resto));
  return partes.join(' ');
};

const PLURAL = { PEN: 'SOLES', USD: 'DÓLARES AMERICANOS', EUR: 'EUROS', BOB: 'BOLIVIANOS', GTQ: 'QUETZALES', CRC: 'COLONES', PYG: 'GUARANÍES' };

/** 2000 céntimos → «VEINTE CON 00/100 SOLES». */
export const montoEnLetras = (centimos, codigo = monedaActual()) => {
  const moneda = normalizarMoneda(codigo);
  const total = Math.round(Math.abs(Number(centimos) || 0));
  const nombre = PLURAL[moneda] || (MONEDAS[moneda].nombre.startsWith('Peso') ? 'PESOS' : MONEDAS[moneda].nombre.toUpperCase());
  return `${numeroEnLetras(Math.floor(total / 100))} CON ${String(total % 100).padStart(2, '0')}/100 ${nombre}`;
};

// ── Modelo ─────────────────────────────────────────────────────────────────────
const aFecha = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(valor)) return { fecha: valor, hora: '' };
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [a, m, d] = valor.split('-');
    return { fecha: `${d}/${m}/${a}`, hora: '' };
  }
  const f = valor?.toDate ? valor.toDate() : new Date(valor);
  if (Number.isNaN(f.getTime())) return null;
  const zona = { timeZone: 'America/Lima' };
  return {
    fecha: f.toLocaleDateString('es-PE', { ...zona, day: '2-digit', month: '2-digit', year: 'numeric' }),
    hora: f.toLocaleTimeString('es-PE', { ...zona, hour: '2-digit', minute: '2-digit' }),
  };
};

/** Emisor del ticket: nombre y logo del gym, más lo configurado en Ajustes. */
export const emisorTicket = (gym = {}) => {
  const t = { ...DATOS_TICKET_VACIO, ...(gym.ticket || {}) };
  return {
    nombre: texto(gym.nombre, 80) || 'Gimnasio',
    razonSocial: t.razonSocial,
    ruc: t.ruc,
    direccion: t.direccion || texto(gym.direccion, 160),
    telefono: t.telefono, correo: t.correo, web: t.web,
    logo: gym.logo || '',
    mensaje: t.mensaje || MENSAJE_POR_DEFECTO,
    mostrarIgv: t.mostrarIgv !== false,
  };
};

const METODOS = { efectivo: 'Efectivo', yape: 'Yape', plin: 'Plin', tarjeta: 'Tarjeta', transferencia: 'Transferencia', mercadopago: 'Mercado Pago' };
const metodoLegible = (m) => METODOS[String(m || '').toLowerCase()] || (m ? String(m) : '');

const totalesDe = ({ totalCentimos, baseCentimos, igvCentimos, conIgv }) => {
  const total = Math.round(Number(totalCentimos) || 0);
  if (!conIgv) return { totalCentimos: total, conIgv: false };
  const base = Number.isFinite(baseCentimos) ? baseCentimos : Math.round(total * 100 / 118);
  return { totalCentimos: total, gravadaCentimos: base, igvCentimos: Number.isFinite(igvCentimos) ? igvCentimos : total - base, conIgv: true };
};

/**
 * Ticket de un pago o una venta (el «origen» que usan Pagos, Facturación y el POS).
 * origen: { tipo: 'pago'|'venta', referenciaInterna, estado, fecha, clienteNombre, clienteDocumento,
 *           metodoPago, voucherTarjeta, sinIgv, calculo: { items, totalCentimos, baseCentimos, igvCentimos } }
 */
export const modeloTicket = (origen = {}, gym = {}) => {
  const emisor = emisorTicket(gym);
  const momento = aFecha(origen.fecha);
  const unidadPorDefecto = origen.tipo === 'pago' ? 'SERV' : 'UND';
  const items = (origen.calculo?.items || []).map((item) => {
    const cantidad = Number(item.cantidadMilesimas || 1000) / 1000;
    const totalCentimos = Math.round(Number(item.totalCentimos) || 0);
    return {
      cantidad, unidad: item.unidad === 'ZZ' ? 'SERV' : item.unidad === 'NIU' ? 'UND' : unidadPorDefecto,
      codigo: texto(item.codigo, 20), descripcion: texto(item.descripcion, 200) || 'Concepto',
      precioCentimos: Number.isFinite(item.precioCentimos) ? item.precioCentimos : Math.round(totalCentimos / (cantidad || 1)),
      totalCentimos,
    };
  });
  const total = Number.isFinite(origen.calculo?.totalCentimos) ? origen.calculo.totalCentimos : items.reduce((s, i) => s + i.totalCentimos, 0);
  return {
    clase: 'ticket',
    titulo: origen.tipo === 'pago' ? 'TICKET DE PAGO' : 'TICKET DE VENTA',
    numero: texto(origen.referenciaInterna, 40),
    anulada: origen.estado === 'anulada',
    emisor: { ...emisor, encabezado: emisor.nombre, subtitulo: emisor.razonSocial, ruc: emisor.ruc },
    cliente: origen.clienteNombre === null ? null : { nombre: texto(origen.clienteNombre, 120) || 'Público general', documento: texto(origen.clienteDocumento, 30) },
    fecha: momento?.fecha || '', hora: momento?.hora || '',
    items,
    totales: totalesDe({ totalCentimos: total, baseCentimos: origen.calculo?.baseCentimos, igvCentimos: origen.calculo?.igvCentimos,
      conIgv: emisor.mostrarIgv && !origen.sinIgv }),
    son: montoEnLetras(total),
    pago: { metodo: metodoLegible(origen.metodoPago), voucher: texto(origen.voucherTarjeta, 40), condicion: 'CONTADO' },
    simbolo: simboloMoneda(),
    pie: [emisor.mensaje],
  };
};

const NOMBRE_DOC = { DNI: 'DNI', RUC: 'RUC', CE: 'C.E.', PASAPORTE: 'PASAPORTE' };

/** Representación impresa de una boleta (03) o factura (01) aceptada por SUNAT. */
export const modeloComprobanteElectronico = (emision, gym = {}) => {
  const contacto = emisorTicket(gym);
  const e = emision.emisor || {};
  const r = emision.receptor || {};
  const factura = emision.tipoCpe === '01';
  const ubicacion = [e.distrito, e.provincia, e.departamento].filter(Boolean).join(' - ');
  const fecha = aFecha(emision.fechaEmision);
  const total = Number(emision.calculo?.totalCentimos) || 0;
  return {
    clase: 'electronico',
    titulo: factura ? 'FACTURA ELECTRÓNICA' : 'BOLETA DE VENTA ELECTRÓNICA',
    numero: `${emision.serie}-${String(emision.numero).padStart(8, '0')}`,
    anulada: false,
    emisor: {
      ...contacto,
      encabezado: e.nombreComercial || e.razonSocial || contacto.nombre,
      subtitulo: e.nombreComercial ? e.razonSocial : '',
      ruc: e.ruc,
      direccion: [e.direccionFiscal, ubicacion].filter(Boolean).join(' · ') || contacto.direccion,
    },
    cliente: {
      nombre: texto(r.nombre, 160) || 'CLIENTES VARIOS',
      documento: r.tipoDocumento ? `${NOMBRE_DOC[r.tipoDocumento] || r.tipoDocumento} ${r.numeroDocumento}` : '',
      direccion: texto(r.direccion, 200),
      esEmpresa: factura,
    },
    fecha: fecha?.fecha || '',
    hora: String(emision.horaEmision || '').slice(0, 5),
    items: (emision.calculo?.items || []).map((item) => {
      const cantidad = Number(item.cantidadMilesimas || 1000) / 1000;
      return {
        cantidad, unidad: item.unidad === 'NIU' ? 'UND' : 'SERV', codigo: texto(item.codigo, 20),
        descripcion: texto(item.descripcion, 200), precioCentimos: Number(item.precioCentimos) || 0, totalCentimos: Number(item.totalCentimos) || 0,
      };
    }),
    totales: { totalCentimos: total, gravadaCentimos: Number(emision.calculo?.baseCentimos) || 0, igvCentimos: Number(emision.calculo?.igvCentimos) || 0, conIgv: true },
    son: String(emision.leyenda || '').replace(/^SON:?\s*/i, '') || montoEnLetras(total, 'PEN'),
    pago: { metodo: '', voucher: '', condicion: 'CONTADO' },
    simbolo: 'S/',
    hash: texto(emision.hashCpe, 120),
    pie: [
      `Representación impresa de la ${factura ? 'FACTURA ELECTRÓNICA' : 'BOLETA DE VENTA ELECTRÓNICA'}.`,
      'Consulte su validez en www.sunat.gob.pe',
      contacto.mensaje,
    ],
  };
};
